import { Header, Icon, ToolTip } from '@greystar/designsystem';
import {
  calculateTMLP,
  formatDecimalString,
  formatPriceDisplay,
  getAllMonthlyRequiredEssentialFees,
  getOneTimeApplicationFees,
  getOneTimeMoveInFees,
  getOneTimeMoveOutFees,
  getPersonalizedAddonGroupMap,
  hasActiveCount,
  useFormattedAddress,
  useFeesAnalytics,
  useAnalytics,
} from '@greystar/lib';
import { EventProperty } from '@greystar/lib/src/types/property-types';
import { useSitecoreContext } from '@sitecore-jss/sitecore-jss-nextjs';
import CalculateCostsHeader from '../../CalculateCostsHeader/CalculateCostsHeader';
import FeeAccordion from '../../FeeAccordion/FeeAccordion';
import TmlpLabel from '../../TmlpLabel/TmlpLabel';
import LeaseDetailsSection from '../CalculateStep/LeaseDetailsSection/LeaseDetailsSection';
import styles from './summaryStep.module.scss';
import SummaryStepProps from './SummaryStep.props';

export default function SummaryStep(props: SummaryStepProps) {
  const moveOutFees = getOneTimeMoveOutFees(props.feeState.allFees);
  const moveInFees = getOneTimeMoveInFees(props.feeState.allFees, 3);
  const oneTimeApplicationFees = getOneTimeApplicationFees(props.feeState.allFees, 3);
  const monthlyRequiredEssentialFees = getAllMonthlyRequiredEssentialFees(props.feeState.allFees);
  const feeMap = getPersonalizedAddonGroupMap(props.feeState.allFees);
  const analytics = useAnalytics();
  const { mergeAnalyticsData } = useFeesAnalytics();
  const { sitecoreContext } = useSitecoreContext();
  const property: EventProperty = sitecoreContext?.property ?? {};
  const propertyId = property.id ?? '';
  const propertyName = property.name ?? '';
  const state = property?.location?.state ?? '';
  const property_location = useFormattedAddress(property.location);

  const tmlp = calculateTMLP(
    props.feeState.selectedBaseRent,
    props.feeState.allFees,
    props.feeState.numberOfApplicants,
    props.feeState.selectedMoveInDate
  );

  const hasPersonalizedAddOns = hasActiveCount(feeMap);

  const beforeMoveInDescription =
    'Includes one-time charges due at the time of application unless specified otherwise in the application agreement.';
  const atMoveInDescription =
    'Includes one-time charges due at move-in unless specified otherwise in the application or lease agreement. Excludes base rent, all monthly mandatory, any user-select optional fees, variable, usage-based and required fees.';
  const atMoveOutDescription =
    'Includes one-time charges due at the time of move-out and excludes base rent, all monthly mandatory and any user-select optional fees, and variable, usage-based and required fees.';
  const requiredEssentialsDescription = 'Includes required, usage-based, or variable fees.';
  const personalizedAddOnsDescription =
    'Personalized add-ons are optional, some subject to availability, which will include additional one-time or recurring costs.';
  const baseRentDescription = 'Amount is based on selected move-in date and lease term.';

  const handleTooltipClick = (label: string) => {
    analytics.trackEvent('tooltip', {
      action_type: 'click',
      state: state,
      property_location,
      property_id: propertyId,
      property_name: propertyName,
      content_group: label,
      module_name: 'fees section',
      inpage_location: 'middle',
      click_text: label,
      destination_url: '',
    });
  };

  const updateTooltipData = (label = '') => {
    mergeAnalyticsData({
      overrides: {
        event: 'unit_rent_calc_tooltip',
        action_type: 'click',
        tooltip: label,
        click_text: label,
      },
      onComplete: (data) => {
        analytics.trackEvent('unit_rent_calc_tooltip', data);
      },
    });
  };

  return (
    <div className={styles['summary-step']}>
      <LeaseDetailsSection {...props} />
      <CalculateCostsHeader activeStep={2} />
      <Header component={'h5'} color={'primary'}>
        One time fees
      </Header>
      {oneTimeApplicationFees.length > 0 && (
        <FeeAccordion
          {...props}
          title="Before Move-in"
          fees={oneTimeApplicationFees}
          isOneTime={true}
          description={beforeMoveInDescription}
          isSummaryStep={true}
        />
      )}
      <FeeAccordion
        {...props}
        title="At Move-in"
        fees={moveInFees}
        isOneTime={true}
        description={atMoveInDescription}
        isSummaryStep={true}
        showProration={true}
      />
      {moveOutFees.length > 0 && (
        <FeeAccordion
          {...props}
          title="At Move-out"
          fees={moveOutFees}
          isOneTime={true}
          description={atMoveOutDescription}
          isSummaryStep={true}
        />
      )}
      <Header component={'h5'} color={'primary'}>
        Total Monthly Leasing Price
      </Header>
      <FeeAccordion
        {...props}
        title="Monthly base rent"
        description={baseRentDescription}
        defaultSubtotal={formatDecimalString(props.feeState.selectedBaseRent)}
        isSummaryStep={true}
        baseRent={formatPriceDisplay(props.feeState.selectedBaseRent)}
      />
      {monthlyRequiredEssentialFees.length > 0 && (
        <FeeAccordion
          {...props}
          title="Monthly Required Essentials"
          fees={monthlyRequiredEssentialFees}
          description={requiredEssentialsDescription}
          isSummaryStep={true}
        />
      )}
      {hasPersonalizedAddOns && (
        <FeeAccordion
          {...props}
          title="Personalized Add-ons"
          feeMap={getPersonalizedAddonGroupMap(props.feeState.allFees)}
          description={personalizedAddOnsDescription}
          isSummaryStep={true}
        />
      )}
      <div className={styles['summary-step-totalContainer']}>
        <div className={styles['summary-step-totalContainer-header']}>
          <Header component={'h5'} color={'primary'}>
            Total
          </Header>
          <ToolTip
            description={<TmlpLabel />}
            elementToHover={<Icon iconName="QuestionMark" size="sm" />}
            tooltipId="tooltip-anchor"
            toolTipClick={() => {
              updateTooltipData('Total');
              handleTooltipClick('Total');
            }}
          />
        </div>
        <div className={styles['summary-step-totalContainer-price']}>
          {formatPriceDisplay(tmlp.amount, tmlp.maxAmount)}
        </div>
      </div>
    </div>
  );
}
